import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import PasswordChecklist from "react-password-checklist";
import { API_BASE_URL } from "../common/config";
import RotatingRisquiKey from "./RotatingRisquiKey";
import AuthService from "../services/auth.service";
import { useAppContext } from "../AppContext";
import LanguageSwitcher from "../common/LanguageSwitcher";

const SetPasswordPage = () => {
  const { t } = useTranslation();
  const [rotation, setRotation] = useState(0);
  const [password, setPassword] = useState("");
  const [passwordAgain, setPasswordAgain] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const token = searchParams.get("token");

  let navigate = useNavigate();

  const { currentUser } = useAppContext();

  // if logged in, log out first to set password
  if (currentUser) {
    AuthService.logout();
    navigate(0);
  }
  if (!userId || !token) {
	return (<div id="login-container">
		<div className="container">
			<div className="col-md-12">
			<div id="login-content"><div className="alert alert-danger" role="alert">{t('set_password.invalid_link')}</div></div>
		</div>
		</div>
	</div>);
  }

  const onChangeRotation = (e) => {
    const formVal = e.target.value;
    setRotation(Math.max(-15, 80 - formVal.length * 5));
  };

  const onChangePasswordAgain = (e) => {
    onChangeRotation(e);
    setPasswordAgain(e.target.value);
  };

  const onChangePassword = (e) => {
    onChangeRotation(e);
    setPassword(e.target.value);
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();

	try {
		setMessage("");
		setLoading(true);
	
		if(!isPasswordValid) {
			setMessage(t('trial_env.password_requirements_error'));
			setLoading(false);
			return;
		}
		
		const response = await AuthService.setPassword(userId, token, password);

		if(response.status !== 200) {
			setMessage(response.data.msg);
			setLoading(false);
			return;
		}

		setMessage(response.data.msg + "\n" + t('set_password.success_redirect'));
		setLoading(false);

		setTimeout(() => {
			navigate("/");	
		}, 3000);
	
	} catch (error) {
		setMessage(error.response.data.msg);
		setLoading(false);
	}
  };

  const handleMicrosoftLogin = () => {
    window.location.href = API_BASE_URL + '/api/auth/microsoft';
  };

  return (
    <div id="login-container">
      <div className="container">
        <div className="col-md-12">
          <div id="login-content">
            <div id="login-logo">
              <i className="icon-risui" />
              <RotatingRisquiKey rotation={rotation} />
            </div>

            <div className="card card-container">
              <LanguageSwitcher />
              <div className="profile-img-card" />
              <h1>{t('set_password.title')}</h1>
              <h3>{t('set_password.subtitle')}</h3>

			  <div className="form-group">
                    <button type="button" className="btn btn-outline-primary btn-block login-submit login-microsoft" onClick={handleMicrosoftLogin}>
                      <span className="microsoft-logo" />{t('set_password.sign_up_with_microsoft')}
                    </button>
                  </div>

            <span className="hr-text">{t('set_password.or')}</span>

              {message && (
                <div className="form-group">
                  <div className={"alert alert-" + (message.includes("success") ? "success" : "danger")} role="alert" style={{ whiteSpace: 'pre-line' }}>
                    {message}
                  </div>
                </div>
              )}

			{!message.includes("success") && (
				<form onSubmit={handleSetPassword}>

					<div className="form-group">
					<label htmlFor="password">{t('set_password.password')}</label>
					<input
						type="password"
						className="form-control"
						name="password"
						placeholder="*********"
						value={password}
						onChange={onChangePassword}
						onBlur={onChangeRotation}
						onFocus={onChangeRotation}
						required
					/>
					</div>

					<div className="form-group">
					<label htmlFor="password">{t('set_password.password_repeat')}</label>
					<input
						type="password"
						className="form-control"
						name="passwordAgain"
						placeholder="*********"
						value={passwordAgain}
						onChange={onChangePasswordAgain}
						onBlur={onChangeRotation}
						onFocus={onChangeRotation}
						required
					/>
					</div>

					{(password || passwordAgain) && (
						<div style={{marginTop: 20}}>
							<PasswordChecklist
								rules={[
								"minLength",
								"specialChar",
								"number",
								"capital",
								"lowercase",
								"match",
								]}
								minLength={12}
								value={password}
								valueAgain={passwordAgain}
								onChange={(isValid) => {
								setIsPasswordValid(isValid);
								}}
								messages={{
									minLength: t('password_min_length'),
									specialChar: t('password_special_char'),
									number: t('password_number'),
									capital: t('password_capital'),
									lowercase: t('password_lowercase'),
									match: t('password_match')
								}}
							/>
						</div>
					)}

					<div className="form-group">
					<button
						className="btn btn-outline-primary btn-block login-submit"
						disabled={loading}
					>
						{loading && (
						<span className="spinner-border spinner-border-sm" />
						)}
						<span>{t('set_password.create_account')}</span>
					</button>
					</div>

				</form>
				)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetPasswordPage;
